import { type MutableRefObject } from 'react';
import { type MenuInputOption } from '@saviynt/design-system';

import type { DataProvider, FormValues } from '../../../../shared/types';

import { FIELD_TYPES } from '../../../../shared/constants';
import { store } from '../../../../shared/store';
import { selectEntityInitialData } from '../../data-store';

// ============================================================================
// Dependency Utilities
// ============================================================================

// Type for initialData to reuse across functions
export type InitialDataForDependencies = {
  requestor?: {
    id?: string;
    userName?: string;
    firstName?: string;
  };
  requestee?: {
    id?: string;
    userName?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    manager?: string;
    displayName?: string;
  };
  loggedInUser?: {
    id?: string;
    userName?: string;
    firstName?: string;
  };
} | null;

const CONTEXTUAL_ENTITY_ROOTS = new Set([
  'requestor',
  'requestee',
  'loggedinuser',
]);

/**
 * True when `depKey` is requestor / requestee / loggedInUser (any casing on root),
 * optionally with a dotted attribute (e.g. requestor.userName).
 */
const isContextualEntityDependencyKey = (depKey: string): boolean => {
  const trimmed = depKey.trim();

  if (trimmed === '') {
    return false;
  }

  const dotIndex = trimmed.indexOf('.');
  const root = dotIndex === -1 ? trimmed : trimmed.slice(0, dotIndex);

  return CONTEXTUAL_ENTITY_ROOTS.has(root.toLowerCase());
};

/**
 * True when the provider lists at least one dependency and every key is a contextual
 * entity (requestor / requestee / loggedInUser, including dotted forms). Used to allow
 * the first options load when initialData is absent; the server may resolve context.
 */
export const isOnlyContextualEntityDependencies = (
  provider: DataProvider
): boolean => {
  const deps = provider.dependencies;

  if (!deps || Object.keys(deps).length === 0) {
    return false;
  }

  return Object.keys(deps).every(isContextualEntityDependencyKey);
};

/**
 * Gets the value for a dependency from either initialData (Redux) or formValues.
 * Contextual keys (requestor / requestee / loggedInUser, any casing on root, optional `.attr`)
 * resolve to that entity's `id` from load/entity APIs for LiveForm — not the dotted attribute value.
 */
const getDependencyValue = (
  depFieldName: string,
  formValues: FormValues,
  initialData?: InitialDataForDependencies
): unknown => {
  if (isContextualEntityDependencyKey(depFieldName)) {
    const data = initialData ?? selectEntityInitialData(store.getState());

    if (!data) {
      return null;
    }

    const trimmed = depFieldName.trim();
    const dotIndex = trimmed.indexOf('.');
    const root = dotIndex === -1 ? trimmed : trimmed.slice(0, dotIndex);
    const rootLower = root.toLowerCase();

    if (rootLower === 'requestor' && data.requestor) {
      return data.requestor.id ?? null;
    }

    if (rootLower === 'requestee' && data.requestee) {
      return data.requestee.id ?? null;
    }

    if (rootLower === 'loggedinuser' && data.loggedInUser) {
      return data.loggedInUser.id ?? null;
    }

    return null;
  }

  return formValues[depFieldName];
};

export const areDependenciesSatisfied = (
  provider: DataProvider,
  formValues: FormValues,
  initialData?: InitialDataForDependencies
): boolean => {
  if (!provider.dependencies) {
    return true;
  }

  const dependencyKeys = Object.keys(provider.dependencies);

  return dependencyKeys.every((depFieldName) => {
    const depValue = getDependencyValue(depFieldName, formValues, initialData);

    // Handle Date objects — a valid Date counts as satisfied
    if (depValue instanceof Date) {
      return !Number.isNaN(depValue.getTime());
    }

    // Handle MenuInputOption format (user selections)
    if (depValue && typeof depValue === 'object' && 'value' in depValue) {
      return Boolean(depValue.value);
    }

    // Handle string format (entity data from API)
    if (typeof depValue === 'string') {
      return depValue.trim() !== '';
    }

    // Handle array format
    if (Array.isArray(depValue) && depValue.length > 0) {
      return true;
    }

    return false;
  });
};

/**
 * Formats a Date object as MM-dd-yyyy for use in data provider dependency values.
 * This matches the format expected by backend SQL/HTTP providers when a date field
 * is used as a dependency (e.g. WHERE clause: `date_col = ${myDateField}`).
 * Exported for use in change-detection hooks (useDependencyValues).
 */
export const formatDateDependencyValue = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();

  return `${month}-${day}-${year}`;
};

// Internal alias used within this file
const formatDateDependency = formatDateDependencyValue;

export const buildDependencies = (
  provider: DataProvider,
  formValues: FormValues,
  initialData?: InitialDataForDependencies,
  /** Optional map of fieldName → fieldType for type-aware value formatting (e.g. date fields). */
  fieldTypeMap?: Map<string, string>
): Record<string, string[]> => {
  const dependencies: Record<string, string[]> = {};

  if (provider.dependencies) {
    Object.keys(provider.dependencies).forEach((depFieldName) => {
      const depValue = getDependencyValue(
        depFieldName,
        formValues,
        initialData
      );

      // Handle Date objects — format as MM-dd-yyyy for date fields
      if (
        depValue instanceof Date &&
        !Number.isNaN(depValue.getTime()) &&
        fieldTypeMap?.get(depFieldName) === FIELD_TYPES.DATE
      ) {
        dependencies[depFieldName] = [formatDateDependency(depValue)];

        return;
      }

      // Handle MenuInputOption format (user selections)
      if (depValue && typeof depValue === 'object' && 'value' in depValue) {
        const optionValue = depValue.value;

        dependencies[depFieldName] = optionValue ? [String(optionValue)] : [];

        return;
      }

      // Handle string format (entity data from API)
      if (typeof depValue === 'string' && depValue.trim() !== '') {
        dependencies[depFieldName] = [depValue.trim()];

        return;
      }

      // Handle array format (MultiSelect)
      if (Array.isArray(depValue)) {
        if (
          depValue.length > 0 &&
          typeof depValue[0] === 'object' &&
          'value' in depValue[0]
        ) {
          // MultiSelect - array of MenuInputOption objects
          dependencies[depFieldName] = depValue.map((item) =>
            String((item as MenuInputOption).value)
          );
        } else {
          // Regular array
          dependencies[depFieldName] = depValue.map(String);
        }

        return;
      }

      dependencies[depFieldName] = [];
    });
  }

  return dependencies;
};

// Track dependency values for cascading
export const getRelevantDependencyValues = (
  dataProvider: DataProvider,
  formValues: FormValues,
  initialData?: InitialDataForDependencies
): string => {
  if (!dataProvider.dependencies) {
    return '';
  }

  const dependencyKeys = Object.keys(dataProvider.dependencies);
  const values: Record<string, unknown> = {};

  dependencyKeys.forEach((depFieldName) => {
    const depValue = getDependencyValue(depFieldName, formValues, initialData);

    // Handle Date objects — serialize as formatted string so change detection
    // is stable and consistent with what buildDependencies sends to the API
    if (depValue instanceof Date && !Number.isNaN(depValue.getTime())) {
      values[depFieldName] = formatDateDependency(depValue);

      return;
    }

    // Handle MenuInputOption format (user selections)
    if (depValue && typeof depValue === 'object' && 'value' in depValue) {
      values[depFieldName] = depValue.value;

      return;
    }

    // Handle string format (entity data from API)
    if (typeof depValue === 'string') {
      values[depFieldName] = depValue.trim();

      return;
    }

    // Handle array format (MultiSelect)
    if (Array.isArray(depValue)) {
      // Check if it's an array of MenuInputOption objects
      if (
        depValue.length > 0 &&
        typeof depValue[0] === 'object' &&
        'value' in depValue[0]
      ) {
        // Extract just the values from MenuInputOption array
        values[depFieldName] = depValue.map(
          (item) => (item as MenuInputOption).value
        );
      } else if (depValue.length === 0) {
        // Empty array
        values[depFieldName] = [];
      } else {
        // Regular array
        values[depFieldName] = depValue;
      }

      return;
    }

    values[depFieldName] = null;
  });

  return JSON.stringify(values);
};

// ============================================================================
// Dependency Tracking Utilities
// ============================================================================

/**
 * Creates a memoized relevant dependency values tracker
 * Prevents unnecessary re-renders when formValues object reference changes
 * but dependency values remain the same
 */
export const createRelevantDependencyValuesTracker = (
  dataProvider: DataProvider,
  formValues: FormValues,
  prevDependencyValuesRef: MutableRefObject<string>,
  prevFormValuesRef: MutableRefObject<string>,
  initialData?: InitialDataForDependencies
): string => {
  const result = getRelevantDependencyValues(
    dataProvider,
    formValues,
    initialData
  );

  // Only return a new value if the actual dependency values changed
  if (prevDependencyValuesRef.current === result) {
    return prevDependencyValuesRef.current;
  }

  // eslint-disable-next-line no-param-reassign
  prevFormValuesRef.current = JSON.stringify(formValues);

  return result;
};
